import { useState } from 'react'
import { Check, Plus, Trash2 } from 'lucide-react'
import { clsx } from 'clsx'
import type { TaskSubtask } from '@/lib/sharedTypes'

interface SubtaskListProps {
  subtasks: TaskSubtask[]
  canEdit: boolean
  onAdd: (title: string) => Promise<void>
  onToggle: (id: string, currentCompleted: boolean) => Promise<void>
  onDelete: (id: string, wasCompleted: boolean) => Promise<void>
}

export function SubtaskList({ subtasks, canEdit, onAdd, onToggle, onDelete }: SubtaskListProps) {
  const [newTitle, setNewTitle] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  const completedCount = subtasks.filter((s) => s.isCompleted).length
  const totalCount = subtasks.length
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  const handleAdd = async () => {
    const trimmed = newTitle.trim()
    if (!trimmed) return
    setIsAdding(true)
    try {
      await onAdd(trimmed)
      setNewTitle('')
    } finally {
      setIsAdding(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div className="space-y-3">
      {/* Progress */}
      {totalCount > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs text-caption font-medium">
            {completedCount}/{totalCount} 완료
          </span>
          <div className="h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Subtask items */}
      <div className="space-y-1">
        {subtasks.map((subtask) => (
          <div
            key={subtask.id}
            className="flex items-center gap-2.5 p-2 rounded-lg hover-surface transition-colors group"
          >
            {/* Checkbox */}
            <button
              onClick={() => canEdit && onToggle(subtask.id, subtask.isCompleted)}
              disabled={!canEdit}
              className={clsx(
                'w-4.5 h-4.5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors',
                subtask.isCompleted
                  ? 'bg-green-500 border-green-500'
                  : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
              )}
              style={{ width: 18, height: 18 }}
            >
              {subtask.isCompleted && <Check className="w-3 h-3 text-white" />}
            </button>

            {/* Title */}
            <span
              className={clsx(
                'flex-1 text-sm min-w-0 truncate',
                subtask.isCompleted ? 'line-through text-disabled' : 'text-heading'
              )}
            >
              {subtask.title}
            </span>

            {/* Delete */}
            {canEdit && (
              <button
                onClick={() => onDelete(subtask.id, subtask.isCompleted)}
                className="p-0.5 rounded text-disabled hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add input */}
      {canEdit && (
        <div className="flex items-center gap-2 pt-1">
          <Plus className="w-4 h-4 text-disabled flex-shrink-0" />
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="하위 항목 추가..."
            disabled={isAdding}
            className={clsx(
              'flex-1 text-sm bg-transparent border-none outline-none',
              'text-heading placeholder:text-disabled'
            )}
          />
          {newTitle.trim() && (
            <button
              onClick={handleAdd}
              disabled={isAdding}
              className="text-xs text-primary-500 hover:text-primary-600 font-medium flex-shrink-0"
            >
              추가
            </button>
          )}
        </div>
      )}
    </div>
  )
}
